import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Reveal } from '../components';
import { usePageContext } from '../contexts';

export const ManifestoPage = () => {
  const { navigateTo } = usePageContext();

  return (
    <div className="bg-zinc-950 text-zinc-100 min-h-screen selection:bg-orange-500 selection:text-white pt-28 sm:pt-36 pb-24 sm:pb-32 font-sans relative overflow-hidden">
      {/* Background ambient lighting */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-gradient-to-b from-orange-500/10 via-red-500/5 to-transparent blur-3xl pointer-events-none -z-10" />

      {/* Hero Header */}
      <div className="max-w-4xl mx-auto px-6 mb-20 sm:mb-28 text-center">
        <Reveal>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-8 border border-zinc-800 bg-zinc-900/60 rounded-full text-zinc-400 font-mono text-xs uppercase tracking-[0.25em] backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
            Manifesto 001
          </div>
          
          <h1 className="text-5xl sm:text-7xl md:text-8xl lg:text-9xl font-extrabold text-white tracking-tighter leading-[0.95] mb-8">
            The Great <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-amber-300 to-red-500">
              Reconnection
            </span>
          </h1>

          <p className="text-xl sm:text-2xl md:text-3xl text-zinc-400 font-serif italic max-w-2xl mx-auto leading-relaxed">
            A declaration of dependence on the physical world.
          </p>
        </Reveal>
      </div>

      <div className="max-w-4xl mx-auto px-6 space-y-16 sm:space-y-24">

        {/* Section I: The Paradox */}
        <Reveal>
          <section className="bg-zinc-900/70 border border-zinc-800/80 rounded-2xl p-8 sm:p-12 md:p-16 backdrop-blur-sm relative">
            <div className="flex items-center gap-4 mb-8 pb-6 border-b border-zinc-800">
              <span className="text-4xl sm:text-5xl font-extrabold text-orange-500 font-mono">I.</span>
              <h2 className="text-2xl sm:text-4xl font-bold text-white tracking-tight">The Paradox</h2>
            </div>

            <div className="space-y-6 text-zinc-300 text-lg sm:text-xl leading-relaxed">
              <p className="text-2xl sm:text-3xl text-white font-medium">
                We built tools to connect the world.
              </p>

              <p className="text-xl text-orange-400 font-mono italic">
                And yet—
              </p>

              <div className="py-4 border-l-2 border-orange-500/80 pl-6 my-6 bg-zinc-950/40 rounded-r-lg">
                <p className="text-2xl sm:text-3xl font-light text-zinc-200">
                  We have never been more connected.
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-white mt-1">
                  We have never been more alone.
                </p>
              </div>

              <p>
                The internet promised a global village. <br />
                <span className="text-white font-semibold">What it delivered was a hall of mirrors.</span>
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 my-8">
                <div className="bg-zinc-950/60 p-5 rounded-xl border border-zinc-800/80">
                  <p className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-2">Trade Off 01</p>
                  <p className="text-zinc-200 font-medium">We trade dopamine for oxytocin.</p>
                </div>
                <div className="bg-zinc-950/60 p-5 rounded-xl border border-zinc-800/80">
                  <p className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-2">Trade Off 02</p>
                  <p className="text-zinc-200 font-medium">We trade followers for friends.</p>
                </div>
                <div className="bg-zinc-950/60 p-5 rounded-xl border border-zinc-800/80">
                  <p className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-2">Trade Off 03</p>
                  <p className="text-zinc-200 font-medium">We trade the friction of real life for the ease of simulation.</p>
                </div>
              </div>

              <div className="text-xl sm:text-2xl text-zinc-300 font-serif">
                <p className="mb-1">Convenience scaled.</p>
                <p className="text-white font-bold">Connection didn’t.</p>
              </div>

              <p className="text-zinc-400">
                Face-to-face time is declining. Loneliness is rising.
              </p>

              <div className="mt-8 p-6 bg-red-950/30 border border-red-900/40 rounded-xl">
                <p className="text-xl sm:text-2xl font-bold text-red-200">
                  At scale, this is not a product flaw. It’s a system failure.
                </p>
              </div>
            </div>
          </section>
        </Reveal>

        {/* Section II: The Shift */}
        <Reveal>
          <section className="bg-zinc-900/70 border border-zinc-800/80 rounded-2xl p-8 sm:p-12 md:p-16 backdrop-blur-sm">
            <div className="flex items-center gap-4 mb-8 pb-6 border-b border-zinc-800">
              <span className="text-4xl sm:text-5xl font-extrabold text-orange-500 font-mono">II.</span>
              <h2 className="text-2xl sm:text-4xl font-bold text-white tracking-tight">The Shift</h2>
            </div>

            <div className="space-y-6 text-zinc-300 text-lg sm:text-xl leading-relaxed">
              <p className="text-xl text-zinc-400 uppercase tracking-widest font-mono">
                Now comes the next wave.
              </p>

              <h3 className="text-4xl sm:text-5xl font-black text-white tracking-tight">
                Generative AI.
              </h3>

              <div className="flex flex-wrap gap-3 my-6">
                <span className="px-4 py-2 bg-zinc-950 border border-zinc-800 rounded-full text-zinc-300 text-sm font-medium">
                  Infinite content.
                </span>
                <span className="px-4 py-2 bg-zinc-950 border border-zinc-800 rounded-full text-zinc-300 text-sm font-medium">
                  Infinite identities.
                </span>
                <span className="px-4 py-2 bg-zinc-950 border border-zinc-800 rounded-full text-zinc-300 text-sm font-medium">
                  Infinite distraction.
                </span>
              </div>

              <p className="text-xl sm:text-2xl text-zinc-200 leading-snug">
                The boundary between real and artificial is no longer blurring—<span className="text-white font-bold underline decoration-orange-500 underline-offset-4">it is dissolving.</span>
              </p>

              <div className="mt-8 p-8 bg-gradient-to-r from-zinc-950 via-zinc-900 to-zinc-950 border border-zinc-800 rounded-xl text-center">
                <p className="text-xl text-zinc-400 mb-2">We are not entering the uncanny valley.</p>
                <p className="text-3xl sm:text-4xl font-black text-white tracking-tight">
                  We are living in it.
                </p>
              </div>
            </div>
          </section>
        </Reveal>

        {/* Section III: A Different Bet */}
        <Reveal>
          <section className="bg-zinc-900/70 border border-zinc-800/80 rounded-2xl p-8 sm:p-12 md:p-16 backdrop-blur-sm">
            <div className="flex items-center gap-4 mb-8 pb-6 border-b border-zinc-800">
              <span className="text-4xl sm:text-5xl font-extrabold text-orange-500 font-mono">III.</span>
              <h2 className="text-2xl sm:text-4xl font-bold text-white tracking-tight">A Different Bet</h2>
            </div>

            <div className="space-y-6 text-zinc-300 text-lg sm:text-xl leading-relaxed">
              <p className="text-2xl sm:text-3xl font-extrabold text-white">
                We reject the premise.
              </p>

              <p className="text-xl sm:text-2xl text-zinc-200">
                The future is not about escaping reality. It’s about <span className="text-orange-400 font-semibold">restoring it.</span>
              </p>

              <div className="p-6 bg-zinc-950/80 border-l-4 border-orange-500 rounded-r-xl my-6">
                <p className="text-xl sm:text-2xl text-white font-medium">
                  Technology should not be the destination. It should be the bridge.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
                <div className="bg-zinc-950/80 p-6 rounded-xl border border-zinc-800">
                  <p className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-2">The Old System</p>
                  <p className="text-zinc-400 leading-relaxed">
                    Old systems compete for attention. They maximize time-on-screen.
                  </p>
                </div>
                <div className="bg-zinc-900 p-6 rounded-xl border border-orange-500/40 bg-gradient-to-b from-orange-500/5 to-transparent">
                  <p className="text-xs uppercase tracking-widest text-orange-400 font-mono mb-2">How We Build</p>
                  <p className="text-white font-semibold leading-relaxed mb-1">
                    We build differently.
                  </p>
                  <p className="text-zinc-200 leading-relaxed">
                    We compete for presence. We remove friction so people can meet—not scroll.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </Reveal>

        {/* Section IV: Our Vow */}
        <Reveal>
          <section className="bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-950 border border-orange-500/30 rounded-2xl p-8 sm:p-12 md:p-16 backdrop-blur-sm relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 blur-3xl pointer-events-none" />

            <div className="flex items-center gap-4 mb-8 pb-6 border-b border-zinc-800">
              <span className="text-4xl sm:text-5xl font-extrabold text-orange-500 font-mono">IV.</span>
              <h2 className="text-2xl sm:text-4xl font-bold text-white tracking-tight">Our Vow</h2>
            </div>

            <div className="space-y-8">
              <div className="text-2xl sm:text-3xl font-bold text-white">
                Panometra is built for the <span className="text-orange-400">Experience Economy.</span>
              </div>

              <div className="bg-zinc-950 p-8 rounded-xl border border-zinc-800 text-center">
                <p className="text-sm font-mono uppercase tracking-widest text-zinc-400 mb-3">
                  We measure what others ignore:
                </p>
                <p className="text-4xl sm:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-amber-200 to-white tracking-tight">
                  Time spent offline.
                </p>
              </div>

              <div className="space-y-4">
                <p className="text-xs uppercase tracking-[0.25em] font-mono text-zinc-400 font-semibold">
                  This is our filter:
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-6 bg-zinc-950/80 rounded-xl border border-zinc-800/80">
                    <p className="text-zinc-400 text-base">If it isolates,</p>
                    <p className="text-zinc-100 font-bold text-xl mt-1">we don’t build it.</p>
                  </div>
                  <div className="p-6 bg-zinc-950/80 rounded-xl border border-zinc-800/80">
                    <p className="text-zinc-400 text-base">If it addicts,</p>
                    <p className="text-zinc-100 font-bold text-xl mt-1">we don’t fund it.</p>
                  </div>
                  <div className="p-6 bg-orange-950/30 rounded-xl border border-orange-500/50">
                    <p className="text-orange-300 text-base">If it brings people together,</p>
                    <p className="text-white font-extrabold text-xl mt-1">we go all in.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </Reveal>

        {/* Section V: The Work */}
        <Reveal>
          <section className="bg-zinc-900/70 border border-zinc-800/80 rounded-2xl p-8 sm:p-12 md:p-16 backdrop-blur-sm">
            <div className="flex items-center gap-4 mb-8 pb-6 border-b border-zinc-800">
              <span className="text-4xl sm:text-5xl font-extrabold text-orange-500 font-mono">V.</span>
              <h2 className="text-2xl sm:text-4xl font-bold text-white tracking-tight">The Work</h2>
            </div>

            <div className="space-y-8 text-zinc-300 text-lg sm:text-xl leading-relaxed">
              <p className="text-2xl sm:text-3xl text-white font-semibold">
                We partner with a different kind of founder.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 font-mono text-center">
                <div className="p-4 bg-zinc-950 rounded-lg border border-zinc-800 text-orange-400 font-bold uppercase tracking-wider text-sm">
                  Relentless.
                </div>
                <div className="p-4 bg-zinc-950 rounded-lg border border-zinc-800 text-orange-400 font-bold uppercase tracking-wider text-sm">
                  Independent.
                </div>
                <div className="p-4 bg-zinc-950 rounded-lg border border-zinc-800 text-orange-400 font-bold uppercase tracking-wider text-sm">
                  Grounded in reality.
                </div>
              </div>

              <p className="text-xl text-zinc-300">
                From idea to scale, we build companies that do one thing well:
              </p>

              <div className="p-8 bg-gradient-to-r from-orange-500 to-red-600 rounded-xl text-white text-center shadow-xl shadow-orange-950/20">
                <p className="text-3xl sm:text-5xl font-black tracking-tight">
                  Make people more human.
                </p>
              </div>
            </div>
          </section>
        </Reveal>

        {/* Footer Signature */}
        <Reveal>
          <div className="pt-12 border-t border-zinc-800 flex flex-col sm:flex-row items-center justify-between gap-8">
            <div className="text-center sm:text-left">
              <h3 className="text-2xl sm:text-3xl font-serif italic text-white font-bold mb-1">
                The Panometra Partners
              </h3>
              <p className="text-xs uppercase tracking-[0.3em] font-mono text-orange-400 font-semibold">
                Invest in reality.
              </p>
            </div>

            <button
              onClick={() => navigateTo('apply')}
              className="inline-flex items-center gap-3 bg-white text-zinc-950 px-8 py-4 rounded-full text-xs font-bold uppercase tracking-[0.2em] hover:bg-orange-500 hover:text-white transition-all transform hover:scale-105 shadow-lg"
            >
              Partner With Us
              <ArrowRight size={16} />
            </button>
          </div>
        </Reveal>

      </div>
    </div>
  );
};
